import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_MS = 30_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const inboxRoute = (user?.role === "admin" || user?.role === "super_admin")
    ? "/admin/notifications"
    : user?.role === "employee"
      ? "/employee/notifications"
      : "/dashboard/notifications";

  const fetchCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data.count || 0);
    } catch (err) {
      console.warn("Notification count fetch failed:", err);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    // Real-time refresh when the WS toaster receives a push
    const onPush = () => fetchCount();
    window.addEventListener("pb:notification", onPush);
    return () => { clearInterval(t); window.removeEventListener("pb:notification", onPush); };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggleOpen = () => {
    if (!open) fetchList();
    setOpen(!open);
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) await api.post(`/notifications/${n.notification_id}/read`);
    } catch (err) {
      console.warn("Mark-as-read failed:", err);
    }
    setOpen(false);
    fetchCount();
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.warn("Mark-all-read failed:", err);
    }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={toggleOpen}
        data-testid="notification-bell-btn"
        className="relative w-11 h-11 md:w-9 md:h-9 rounded-md grid place-items-center text-white/80 hover:text-white hover:bg-white/5 transition"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#C9A356] text-[#0A2540] text-[10px] font-bold grid place-items-center px-1"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute left-0 mt-2 w-[340px] bg-white text-[#0A2540] rounded-md shadow-2xl border border-[#E2E8F0] overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between bg-slate-50">
            <div className="font-heading font-medium text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-xs text-[#C9A356] hover:underline font-medium"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {loading && <div className="px-4 py-6 text-sm text-[#475569]">Loading...</div>}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-[#475569]">
                <Bell className="w-8 h-8 mx-auto text-[#E2E8F0] mb-2" />
                You're all caught up!
              </div>
            )}
            {!loading && items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.notification_id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50 transition ${
                  !n.read ? "bg-[#FFFBF0]" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 mt-1.5 rounded-full shrink-0 ${!n.read ? "bg-[#C9A356]" : "bg-transparent"}`}></div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${!n.read ? "font-semibold" : "font-medium"} text-[#0A2540]`}>
                      {n.title}
                    </div>
                    <div className="text-xs text-[#475569] mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {n.read && <Check className="w-3 h-3 text-[#94A3B8] shrink-0 mt-1" />}
                </div>
              </button>
            ))}
          </div>
          <button
            onClick={() => { setOpen(false); navigate(inboxRoute); }}
            data-testid="notification-see-all"
            className="w-full px-4 py-2.5 text-xs font-semibold text-[#0A2540] hover:bg-slate-50 border-t border-[#E2E8F0] inline-flex items-center justify-center gap-1.5 transition"
          >
            See all notifications <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
